import type { NomeDoIcone } from '../design/Icone';

/**
 * O MENU DO ADMINISTRADOR — tela 02 do design, na ordem e nos grupos do mockup.
 *
 * Cada item diz em que BLOCO do roadmap (docs/roadmap/BLOCOS-INOVAPAY-2.md) a tela dele chega.
 * Enquanto não chega, a rota abre uma página que diz isso — nunca uma tela de mentira com
 * números de exemplo.
 *
 * 🚨 O menu é UX, não segurança: esconder um item não impede ninguém de chamar a API. Quem
 * decide o que cada perfil pode é o servidor (regra 19). Quando o perfil chegar (Bloco 2),
 * o menu passa a mostrar só o que a sessão permite — e o servidor continua recusando o resto.
 */

export type ItemDoMenu = {
  readonly rota: string;
  readonly rotulo: string;
  readonly icone: NomeDoIcone;
  readonly bloco: number;
  /** Frase curta do que a tela vai ser, para a página "chega no bloco N". */
  readonly descricao: string;
  /**
   * Capacidade que a sessão precisa ter para o item APARECER. Só esconde — quem recusa é o
   * servidor. Sem capacidade: aparece para todos do shell do administrador.
   */
  readonly capacidade?: string;
};

export type GrupoDoMenu = {
  /**
   * Zona da lateral: "donos" = dados da LOJA (faturamento, custo, lucro — só dono/dono_titular,
   * `loja.ver`); "inovapay" = a administração dos programas. Só separa na TELA; quem decide é a
   * capacidade de cada item (e o servidor).
   */
  readonly zona?: 'donos' | 'inovapay';
  readonly titulo: string | null;
  readonly itens: readonly ItemDoMenu[];
};

export const MENU: readonly GrupoDoMenu[] = [
  {
    zona: 'donos',
    titulo: null,
    itens: [
      {
        rota: 'visao-geral',
        rotulo: 'Visão geral',
        icone: 'grid',
        bloco: 12,
        descricao: 'A operação do dia: vendas, premiação, meta, Giro e divergências.',
        capacidade: 'loja.ver',
      },
      {
        rota: 'dashboard',
        rotulo: 'Dashboard',
        icone: 'gauge',
        bloco: 12,
        descricao: 'O movimento do período contra uma base explícita, com gráficos e insights.',
        capacidade: 'loja.ver',
      },
    ],
  },
  {
    zona: 'donos',
    titulo: 'LOJA',
    itens: [
      {
        rota: 'vendas',
        rotulo: 'Vendas',
        icone: 'receipt',
        bloco: 9,
        descricao: 'As vendas da farmácia: movimentação, devoluções, exceções e margem.',
        capacidade: 'loja.ver',
      },
      {
        rota: 'lucro-inovapay',
        rotulo: 'Lucro do InovaPay',
        icone: 'money',
        bloco: 9,
        descricao:
          'Quanto a farmácia ganhou com vitamina, pré-vencido e paradão — por programa e por balconista.',
        capacidade: 'loja.ver',
      },
      {
        rota: 'vida-do-produto',
        rotulo: 'Vida do produto',
        icone: 'clock',
        bloco: 9,
        descricao:
          'Tudo o que aconteceu com um produto: entrada, venda, ajuste, Giro, divergência e conferência do estoque.',
        capacidade: 'loja.ver',
      },
      {
        rota: 'quadro-de-horario',
        rotulo: 'Quadro de horário',
        icone: 'cal',
        bloco: 9,
        descricao:
          'A escala da loja: turnos, folgas, domingos e feriados, férias, faltas, atestados e os pedidos da equipe.',
        capacidade: 'escala.editar',
      },
    ],
  },
  {
    zona: 'donos',
    titulo: 'ANÁLISE',
    itens: [
      {
        rota: 'relatorios',
        rotulo: 'Relatórios',
        icone: 'file',
        bloco: 12,
        descricao: 'Gráficos, insights e relatórios exportáveis.',
        capacidade: 'loja.ver',
      },
      {
        rota: 'desempenho',
        rotulo: 'Desempenho',
        icone: 'gauge',
        bloco: 12,
        descricao: 'Dashboard gerencial e a análise por balconista — sem virar ranking.',
        capacidade: 'loja.ver',
      },
    ],
  },
  // ━━ os programas (vitamina, pré-vencido, paradão), o Giro e a premiação ━━
  {
    zona: 'inovapay',
    titulo: 'OPERAÇÃO',
    itens: [
      {
        rota: 'inovapay',
        rotulo: 'Painel do InovaPay',
        icone: 'panel',
        bloco: 8,
        descricao: 'A semana dos programas: lançado, conferido, a pagar e quem precisa de atenção.',
        capacidade: 'premiacao.ver_equipe',
      },
      {
        rota: 'vitaminas',
        rotulo: 'Vitaminas',
        icone: 'pill',
        bloco: 4,
        descricao: 'Tudo da vitamina: produtos e faixas, lançamentos, conferência e ranking.',
        capacidade: 'produtos.consultar',
      },
      {
        rota: 'pre-vencidos',
        rotulo: 'Pré-vencidos',
        icone: 'clock',
        bloco: 5,
        descricao: 'Tudo do pré-vencido: lotes e validades, lançamentos, fotos e conferência.',
        capacidade: 'produtos.consultar',
      },
      {
        rota: 'paradao',
        rotulo: 'Paradão',
        icone: 'box',
        bloco: 6,
        descricao: 'Tudo do paradão: produtos sem venda, lançamentos e conferência.',
        capacidade: 'produtos.consultar',
      },
      {
        rota: 'giro',
        rotulo: 'Giro de Prateleira',
        icone: 'cycle',
        bloco: 10,
        descricao: 'Operação do dia, geração, equipe e rodízio dos blocos.',
        capacidade: 'giro.administrar',
      },
      {
        rota: 'encomendas',
        rotulo: 'Encomendas',
        icone: 'box',
        bloco: 16,
        descricao: 'Consultar quem tem, encomendar para o cliente e acompanhar até a entrega.',
        capacidade: 'encomendas.consultar',
      },
      {
        // Dono 03/10: logo abaixo de Encomendas — o que o cliente procurou e a loja não tinha.
        rota: 'encomendas-faltas',
        rotulo: 'Falta',
        icone: 'falta',
        bloco: 16,
        descricao: 'Produtos que o cliente procurou e a loja não tinha, registrados no balcão.',
        capacidade: 'encomendas.ver_faltas',
      },
      {
        rota: 'lancamentos',
        rotulo: 'Lançamentos',
        icone: 'scan',
        bloco: 9,
        descricao: 'Tudo o que os balconistas lançaram nos programas, com o status do cheque.',
        capacidade: 'vendas.ver_todas',
      },
    ],
  },
  {
    zona: 'inovapay',
    titulo: 'PAGAMENTO',
    itens: [
      {
        rota: 'conferencia',
        rotulo: 'Conferência da semana',
        icone: 'check',
        bloco: 8,
        descricao: 'Por vendedor: lançado × vendido no HOS, devoluções, canceladas e a folha.',
        capacidade: 'premiacao.conferir',
      },
      {
        rota: 'premiacao',
        rotulo: 'Premiação',
        icone: 'money',
        bloco: 8,
        descricao: 'O cheque da semana, pagamentos e o comprovante de cada um.',
        capacidade: 'premiacao.ver_equipe',
      },
      {
        rota: 'ranking',
        rotulo: 'Ranking',
        icone: 'trophy',
        bloco: 8,
        descricao: 'Quem mais lançou vitaminas e pré-vencidos no período, com a meta da semana.',
        capacidade: 'vendas.ver_todas',
      },
      {
        rota: 'tudo-por-balconista',
        rotulo: 'Tudo por balconista',
        icone: 'users',
        bloco: 8,
        descricao: 'Vendido e devolvido de vitamina, pré-vencido e paradão por pessoa, e o total.',
        capacidade: 'premiacao.ver_equipe',
      },
    ],
  },
  {
    zona: 'inovapay',
    titulo: 'GESTÃO',
    itens: [
      {
        rota: 'regras',
        rotulo: 'Regras de premiação',
        icone: 'money',
        bloco: 13,
        descricao: 'Regras vigentes por tipo, com versão e vigência, e o simulador.',
        capacidade: 'campanhas.ver',
      },
      {
        rota: 'metas',
        rotulo: 'Metas & Premiação',
        icone: 'gauge',
        bloco: 13,
        descricao:
          'Metas, níveis e bônus mensal: o que paga, quanto e para quem — sem mexer em código.',
        capacidade: 'metas.ver',
      },
      {
        rota: 'campanhas',
        rotulo: 'Campanhas',
        icone: 'trophy',
        bloco: 13,
        descricao: 'Campanhas temporárias sobre produtos existentes, com período e público.',
        capacidade: 'campanhas.ver',
      },
    ],
  },
  {
    zona: 'inovapay',
    titulo: 'CONTROLE',
    itens: [
      {
        rota: 'divergencias',
        rotulo: 'Divergências',
        icone: 'warn',
        bloco: 11,
        descricao: 'A central do auditor: divergência é pergunta, não acusação.',
        capacidade: 'auditoria.ver_todos',
      },
      {
        rota: 'geladeira',
        rotulo: 'Geladeira',
        icone: 'gelo',
        bloco: 11,
        descricao: 'Canetas e alto valor: a conferência do Iggor contra o HOS e cada venda.',
        capacidade: 'geladeira.ver',
      },
      {
        rota: 'controlados',
        rotulo: 'Controlados do dia',
        icone: 'pill',
        bloco: 11,
        descricao:
          'O que saiu do armário ontem: controlados e antibióticos para a farmacêutica conferir.',
        capacidade: 'controlados.conferir',
      },
      {
        rota: 'qualidade',
        rotulo: 'Qualidade do Giro',
        icone: 'shield',
        bloco: 11,
        descricao: 'Qualidade, taxa e bloqueio — sempre com a conta inteira.',
        capacidade: 'giro.administrar',
      },
    ],
  },
  {
    zona: 'inovapay',
    titulo: 'ADMINISTRATIVO',
    itens: [
      {
        rota: 'produtos',
        rotulo: 'Produtos',
        icone: 'box',
        bloco: 4,
        descricao: 'Vitaminas, pré-vencidos, paradão e a camada comercial de cada produto.',
        capacidade: 'produtos.consultar',
      },
      {
        rota: 'comunicacao',
        rotulo: 'Comunicação',
        icone: 'bell',
        bloco: 13,
        descricao: 'Mural e avisos: público, vigência e leituras.',
        capacidade: 'comunicacao.publicar',
      },
      {
        rota: 'equipe',
        rotulo: 'Equipe',
        icone: 'users',
        bloco: 2,
        descricao: 'Pessoas, perfis, permissões e aprovações de acesso.',
        capacidade: 'equipe.ver',
      },
      // Telas 32 e 33: no design elas ficam logo abaixo de Equipe, com o contador de pedidos.
      {
        rota: 'aprovacoes',
        rotulo: 'Aprovações',
        icone: 'check',
        bloco: 2,
        descricao: 'Pedidos de acesso: decisão registrada, com autor, hora e motivo.',
        capacidade: 'equipe.aprovar',
      },
      {
        rota: 'perfis',
        rotulo: 'Perfis e permissões',
        icone: 'shield',
        bloco: 2,
        descricao: 'O que cada perfil pode fazer, como o servidor está configurado.',
        capacidade: 'perfis.ver',
      },
    ],
  },
  {
    zona: 'inovapay',
    titulo: 'SISTEMA',
    itens: [
      {
        rota: 'configuracoes',
        rotulo: 'Configurações',
        icone: 'cog',
        bloco: 13,
        descricao: 'O que está vigente em cada área, e o link para o editor de cada uma.',
        capacidade: 'configuracoes.administrar',
      },
      {
        rota: 'integracoes',
        rotulo: 'Integrações',
        icone: 'wifi',
        bloco: 13,
        descricao: 'De onde vem cada dado, quando chegou e se está saudável.',
        capacidade: 'configuracoes.administrar',
      },
      {
        rota: 'auditoria',
        rotulo: 'Saúde e auditoria',
        icone: 'shield',
        bloco: 13,
        descricao: 'Saúde dos serviços, trilha de auditoria, segurança e dados.',
        capacidade: 'configuracoes.administrar',
      },
    ],
  },
];

/**
 * O MENU DO AUDITOR — tela 22: "quatro abas no módulo, não dezenove". O auditor não administra
 * nada: fila, recontagens, concluídas, histórico e a consulta de produtos.
 */
export const MENU_DO_AUDITOR: readonly GrupoDoMenu[] = [
  {
    titulo: 'EQUIPE',
    itens: [
      {
        rota: 'quadro-de-horario',
        rotulo: 'Quadro de horário',
        icone: 'cal',
        bloco: 9,
        descricao: 'A escala da loja e a minha: turnos, folgas, férias e pedidos.',
      },
    ],
  },
  {
    titulo: 'AUDITORIA',
    itens: [
      {
        rota: 'divergencias',
        rotulo: 'Divergências',
        icone: 'warn',
        bloco: 11,
        descricao: 'A fila do auditor: divergência é pergunta, não acusação.',
      },
      {
        rota: 'recontagens',
        rotulo: 'Recontagens',
        icone: 'scan',
        bloco: 11,
        descricao: 'Casos que pedem recontagem na loja.',
      },
      {
        rota: 'concluidas',
        rotulo: 'Concluídas',
        icone: 'check',
        bloco: 11,
        descricao: 'Casos concluídos, com o resultado e o motivo.',
      },
      {
        rota: 'historico-auditoria',
        rotulo: 'Histórico',
        icone: 'clock',
        bloco: 11,
        descricao: 'Todos os casos do período.',
      },
    ],
  },
  {
    titulo: 'GELADEIRA',
    itens: [
      {
        rota: 'geladeira-conferir',
        rotulo: 'Conferir geladeira',
        icone: 'gelo',
        bloco: 11,
        descricao: 'As canetas da geladeira da farmácia e do estoque de cima, todo dia.',
        capacidade: 'geladeira.conferir',
      },
    ],
  },
  {
    titulo: 'CONSULTA',
    itens: [
      {
        rota: 'produtos',
        rotulo: 'Produtos',
        icone: 'box',
        bloco: 4,
        descricao: 'Catálogo para consulta.',
        capacidade: 'produtos.consultar',
      },
    ],
  },
];

/**
 * O RECEPTOR (0058, dono 02/10): quem recebe a mercadoria na porta. Vê só o Recebimento — nada
 * do financeiro, dos programas ou dos painéis do InovaPay.
 */
export const MENU_DO_RECEPTOR: readonly GrupoDoMenu[] = [
  {
    titulo: 'EQUIPE',
    itens: [
      {
        rota: 'quadro-de-horario',
        rotulo: 'Quadro de horário',
        icone: 'cal',
        bloco: 9,
        descricao: 'A escala da loja e a minha: turnos, folgas, férias e pedidos.',
      },
    ],
  },
  {
    titulo: 'ENCOMENDAS',
    itens: [
      {
        rota: 'encomendas-recebimento',
        rotulo: 'Recebimento e notas',
        icone: 'scan',
        bloco: 16,
        descricao: 'Notas a caminho, que chegaram e que deram entrada; bipar a NF e conferir.',
        capacidade: 'encomendas.receber',
      },
    ],
  },
];

export const TODOS_OS_ITENS: readonly ItemDoMenu[] = [
  ...MENU.flatMap((g) => g.itens),
  ...MENU_DO_AUDITOR.flatMap((g) => g.itens).filter(
    (i) => !MENU.some((g) => g.itens.some((x) => x.rota === i.rota)),
  ),
  ...MENU_DO_RECEPTOR.flatMap((g) => g.itens),
];

const PRIMEIRO: ItemDoMenu = {
  rota: 'visao-geral',
  rotulo: 'Visão geral',
  icone: 'grid',
  bloco: 12,
  descricao: 'A operação do dia: vendas, premiação, meta, Giro e divergências.',
};

/** Rota desconhecida (ou vazia) abre a Visão geral — nunca uma tela em branco. */
export function itemDaRota(rota: string): ItemDoMenu {
  return (
    TODOS_OS_ITENS.find((i) => i.rota === rota) ??
    ITENS_DE_SISTEMA[rota] ??
    TODOS_OS_ITENS[0] ??
    PRIMEIRO
  );
}

/** Rotas de sistema que existem fora do menu (conta e preferências chegam no Bloco 2). */
const ROTAS_DE_SISTEMA: readonly string[] = [
  'encomendas-minhas',
  'encomendas-todas',
  'encomendas-revisar',
  'encomendas-faltas',
  'encomendas-config',
  'encomendas-auditoria',
  'conta',
  'preferencias',
  'perfil',
  'movimentacao',
  'cupom',
  'excecoes',
  'precos',
  'pagamentos',
  'devolucoes',
  'giro-painel',
  'giro-operacao',
  'giro-bloco',
  'giro-geracao',
  'giro-setores',
  'giro-equipe',
  'giro-rodizio',
  'giro-excecoes',
  'giro-ocorrencias',
  'giro-capacidades',
  'giro-bloqueados',
  'giro-relatorio',
  'giro-rupturas',
  'giro-historico',
  'giro-max',
  'giro-config',
  'caso',
  'conferir',
  'divergencias-painel',
  'qualidade-equipe',
  'minhas-auditorias',
  'graficos',
  'insights',
  'tv',
  'mural',
  'vitaminas-produtos',
  'vitaminas-lancamentos',
  'vitaminas-conferencia',
  'vitaminas-ranking',
  'pre-vencidos-produtos',
  'pre-vencidos-vencimentos',
  'pre-vencidos-vencidos',
  'pre-vencidos-alertas',
  'pre-vencidos-lancamentos',
  'pre-vencidos-conferencia',
  'pre-vencidos-ranking',
  'paradao-produtos',
  'paradao-lancamentos',
  'paradao-conferencia',
  'paradao-ranking',
];

/** As páginas da subnav do Giro (telas 16–18) acendem "Giro de Prateleira" na sidebar. */
const DO_GIRO = (rotulo: string, descricao: string): ItemDoMenu => ({
  rota: 'giro',
  rotulo,
  icone: 'cycle',
  bloco: 10,
  descricao,
  capacidade: 'giro.administrar',
});

/** As páginas da subnav de Vendas (telas 13 e 14) acendem "Vendas" na sidebar — é uma entrada só. */
const DA_CENTRAL = (rotulo: string, descricao: string): ItemDoMenu => ({
  rota: 'vendas',
  rotulo,
  icone: 'receipt',
  bloco: 9,
  descricao,
  capacidade: 'vendas.ver_todas',
});

/** Rota vazia é o início. Rota que não está no menu nem no sistema é 404 — nunca a Visão geral calada. */
export function rotaExiste(rota: string): boolean {
  return (
    rota === '' || ROTAS_DE_SISTEMA.includes(rota) || TODOS_OS_ITENS.some((i) => i.rota === rota)
  );
}

export const ITENS_DE_SISTEMA: Readonly<Record<string, ItemDoMenu>> = {
  'encomendas-recebimento': {
    rota: 'encomendas',
    rotulo: 'Encomendas · Recebimento e notas',
    icone: 'box',
    bloco: 16,
    descricao: 'Recebimento das notas do módulo Encomendas.',
    capacidade: 'encomendas.receber',
  },
  'encomendas-minhas': {
    rota: 'encomendas',
    rotulo: 'Encomendas · Minhas encomendas',
    icone: 'box',
    bloco: 16,
    descricao: 'Minhas encomendas do módulo Encomendas.',
    capacidade: 'encomendas.ver_proprias',
  },
  'encomendas-todas': {
    rota: 'encomendas',
    rotulo: 'Encomendas · Todas as encomendas',
    icone: 'box',
    bloco: 16,
    descricao: 'Todas as encomendas do módulo Encomendas.',
    capacidade: 'encomendas.ver_todas',
  },
  'encomendas-revisar': {
    rota: 'encomendas',
    rotulo: 'Encomendas · Vínculos para revisar',
    icone: 'box',
    bloco: 16,
    descricao: 'Vínculos prováveis ou ambíguos com pedido e nota fiscal.',
    capacidade: 'encomendas.vincular',
  },
  'encomendas-faltas': {
    rota: 'encomendas',
    rotulo: 'Encomendas · Faltas registradas',
    icone: 'box',
    bloco: 16,
    descricao: 'Faltas registradas no balcão: ruptura, divergência de estoque e campanhas.',
    capacidade: 'encomendas.ver_faltas',
  },
  'encomendas-config': {
    rota: 'encomendas',
    rotulo: 'Encomendas · Configurações',
    icone: 'box',
    bloco: 16,
    descricao: 'Configurações do módulo Encomendas.',
    capacidade: 'encomendas.configurar',
  },
  'encomendas-auditoria': {
    rota: 'encomendas',
    rotulo: 'Encomendas · Auditoria',
    icone: 'box',
    bloco: 16,
    descricao: 'Auditoria do módulo Encomendas.',
    capacidade: 'encomendas.ver_todas',
  },
  'pre-vencidos-vencimentos': {
    rota: 'pre-vencidos',
    rotulo: 'Pré-vencidos · Vencimentos',
    icone: 'clock',
    bloco: 5,
    descricao: 'Vencimentos do pré-vencido.',
    capacidade: 'produtos.consultar',
  },
  'pre-vencidos-vencidos': {
    rota: 'pre-vencidos',
    rotulo: 'Pré-vencidos · Vencidos por mês',
    icone: 'clock',
    bloco: 5,
    descricao: 'Vencidos por mês do pré-vencido.',
    capacidade: 'produtos.consultar',
  },
  'pre-vencidos-alertas': {
    rota: 'pre-vencidos',
    rotulo: 'Pré-vencidos · Alertas',
    icone: 'clock',
    bloco: 5,
    descricao: 'Alertas do pré-vencido.',
    capacidade: 'produtos.consultar',
  },
  'vitaminas-produtos': {
    rota: 'vitaminas',
    rotulo: 'Vitaminas · Produtos',
    icone: 'pill',
    bloco: 4,
    descricao: 'Produtos do programa vitaminas.',
    capacidade: 'produtos.consultar',
  },
  'vitaminas-lancamentos': {
    rota: 'vitaminas',
    rotulo: 'Vitaminas · Lançamentos',
    icone: 'pill',
    bloco: 4,
    descricao: 'Lançamentos do programa vitaminas.',
    capacidade: 'produtos.consultar',
  },
  'vitaminas-conferencia': {
    rota: 'vitaminas',
    rotulo: 'Vitaminas · Conferência',
    icone: 'pill',
    bloco: 4,
    descricao: 'Conferência do programa vitaminas.',
    capacidade: 'produtos.consultar',
  },
  'vitaminas-ranking': {
    rota: 'vitaminas',
    rotulo: 'Vitaminas · Ranking',
    icone: 'pill',
    bloco: 4,
    descricao: 'Ranking do programa vitaminas.',
    capacidade: 'produtos.consultar',
  },
  'pre-vencidos-produtos': {
    rota: 'pre-vencidos',
    rotulo: 'Pré-vencidos · Produtos',
    icone: 'clock',
    bloco: 5,
    descricao: 'Produtos do programa pré-vencidos.',
    capacidade: 'produtos.consultar',
  },
  'pre-vencidos-lancamentos': {
    rota: 'pre-vencidos',
    rotulo: 'Pré-vencidos · Lançamentos',
    icone: 'clock',
    bloco: 5,
    descricao: 'Lançamentos do programa pré-vencidos.',
    capacidade: 'produtos.consultar',
  },
  'pre-vencidos-conferencia': {
    rota: 'pre-vencidos',
    rotulo: 'Pré-vencidos · Conferência',
    icone: 'clock',
    bloco: 5,
    descricao: 'Conferência do programa pré-vencidos.',
    capacidade: 'produtos.consultar',
  },
  'pre-vencidos-ranking': {
    rota: 'pre-vencidos',
    rotulo: 'Pré-vencidos · Ranking',
    icone: 'clock',
    bloco: 5,
    descricao: 'Ranking do programa pré-vencidos.',
    capacidade: 'produtos.consultar',
  },
  'paradao-produtos': {
    rota: 'paradao',
    rotulo: 'Paradão · Produtos',
    icone: 'box',
    bloco: 6,
    descricao: 'Produtos do programa paradão.',
    capacidade: 'produtos.consultar',
  },
  'paradao-lancamentos': {
    rota: 'paradao',
    rotulo: 'Paradão · Lançamentos',
    icone: 'box',
    bloco: 6,
    descricao: 'Lançamentos do programa paradão.',
    capacidade: 'produtos.consultar',
  },
  'paradao-conferencia': {
    rota: 'paradao',
    rotulo: 'Paradão · Conferência',
    icone: 'box',
    bloco: 6,
    descricao: 'Conferência do programa paradão.',
    capacidade: 'produtos.consultar',
  },
  'paradao-ranking': {
    rota: 'paradao',
    rotulo: 'Paradão · Ranking',
    icone: 'box',
    bloco: 6,
    descricao: 'Ranking do programa paradão.',
    capacidade: 'produtos.consultar',
  },

  'giro-painel': DO_GIRO(
    'Painel do Giro',
    'Cobertura por setor, quem é mais rápido e o que mais gira.',
  ),
  'giro-operacao': DO_GIRO('Operação de hoje', 'O mapa de blocos do setor.'),
  'giro-bloco': DO_GIRO('Detalhe do bloco', 'Etapas, autoria e produtos do bloco.'),
  'giro-geracao': DO_GIRO('Geração', 'Simular, gerar e publicar a operação do dia.'),
  'giro-setores': DO_GIRO('Setores & blocos', 'Quantidade de blocos, alvo e configurações.'),
  'giro-equipe': DO_GIRO('Equipe & presença', 'Quem está hoje e com quais blocos.'),
  'giro-rodizio': DO_GIRO('Rodízio', 'O ciclo de cada pessoa entre os blocos.'),
  'giro-excecoes': DO_GIRO('Exceções da geração', 'O que vendeu e ficou fora da missão.'),
  'giro-ocorrencias': DO_GIRO('Ocorrências do corredor', 'O que a equipe achou na prateleira.'),
  'giro-capacidades': DO_GIRO(
    'Não cabe & bloqueios',
    'Não cabe na prateleira, giro máximo, contagem × HOS e erros de reposição.',
  ),
  'giro-bloqueados': DO_GIRO(
    'Bloqueados',
    'Erro na reposição por balconista, dia a dia, e quem está bloqueado.',
  ),
  'giro-relatorio': DO_GIRO(
    'Relatório de erros',
    'Quanto cada balconista erra, produto a produto, com fotos, bloqueios e o trabalho do Igor/Felipe.',
  ),
  'giro-rupturas': DO_GIRO('Rupturas', 'O que vendeu com estoque zero e ficou fora da missão.'),
  'giro-historico': DO_GIRO('Histórico de operações', 'A foto de cada dia.'),
  // vendas da loja por vendedor e cupom: só quem vê a loja (os donos)
  'giro-max': {
    ...DO_GIRO('Giro Max', 'Produtos que venderam mais de N num único dia, por turno e grupo.'),
    capacidade: 'loja.ver',
  },
  'giro-config': DO_GIRO('Configurações do Giro', 'Parâmetros vigentes e fórmula de reposição.'),
  graficos: {
    rota: 'dashboard',
    rotulo: 'Gráficos',
    icone: 'grid',
    bloco: 12,
    descricao: 'Métrica, dimensão, período e comparação.',
  },
  insights: {
    rota: 'dashboard',
    rotulo: 'Insights',
    icone: 'grid',
    bloco: 12,
    descricao: 'Comparações calculadas, com a conta — nunca causa.',
  },
  caso: {
    rota: 'divergencias',
    rotulo: 'Caso',
    icone: 'warn',
    bloco: 11,
    descricao: 'O caso inteiro: grandezas, linha do tempo, evidências e conclusão.',
  },
  conferir: {
    rota: 'divergencias',
    rotulo: 'Conferência',
    icone: 'scan',
    bloco: 11,
    descricao: 'Conferir prateleira ou estoque: abrir, contar, salvar e passar para o próximo.',
  },
  'divergencias-painel': {
    rota: 'divergencias',
    rotulo: 'Indicadores das divergências',
    icone: 'gauge',
    bloco: 11,
    descricao: 'Pendência, tempo e qualidade das conferências.',
  },
  'qualidade-equipe': {
    rota: 'divergencias',
    rotulo: 'Qualidade da Equipe',
    icone: 'users',
    bloco: 11,
    descricao: 'Erro de contagem (regra dos 30%) e falha de reposição, por balconista.',
  },
  'minhas-auditorias': {
    rota: 'minhas-auditorias',
    rotulo: 'Minhas auditorias',
    icone: 'shield',
    bloco: 11,
    descricao: 'Os seus casos auditados — só os seus.',
  },
  movimentacao: DA_CENTRAL('Movimentação', 'Cupons, devoluções e cancelamentos do período.'),
  cupom: DA_CENTRAL('Cupom', 'O detalhe de um cupom do HOS.'),
  excecoes: DA_CENTRAL('Exceções comerciais', 'Eventos que fogem da média, com a conta explícita.'),
  precos: DA_CENTRAL('Preços & margem', 'Base, PMC, médio e menor preço por produto.'),
  pagamentos: DA_CENTRAL(
    'Formas de pagamento',
    'Dinheiro, PIX, débito, crédito e parcelas lidos do caixa do HOS.',
  ),
  devolucoes: DA_CENTRAL('Devoluções e cancelamentos', 'Eventos distintos, com KPIs próprios.'),
  perfil: {
    rota: 'perfil',
    rotulo: 'Perfil comercial',
    icone: 'user',
    bloco: 8,
    descricao: 'O desempenho comercial de uma pessoa: vendas, premiação e posição.',
  },
  conta: {
    rota: 'conta',
    rotulo: 'Minha conta',
    icone: 'user',
    bloco: 2,
    descricao: 'Seus dados, senha e acesso, preferências e sessões abertas.',
  },
  preferencias: {
    rota: 'preferencias',
    rotulo: 'Preferências',
    icone: 'cog',
    bloco: 2,
    descricao: 'Som ao registrar venda e tema.',
  },
};
